import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Card, Spinner } from 'react-bootstrap';
import { FaBarcode, FaBox, FaCheckCircle, FaShoppingCart } from 'react-icons/fa';
import Avatar from 'components/common/Avatar';

/**
 * Componente para mostrar una tarjeta de producto en el modal de selección
 * Soporta caracteres Unicode (acentos, ñ, símbolos especiales)
 */
const ProductoTarjeta = ({ producto, agregado, cargandoEsteProducto, onAgregarProducto }) => {
    const imagenSrc = producto.vImagenProducto_Base64 
        ? (producto.vImagenProducto_Base64.startsWith("data:image") 
            ? producto.vImagenProducto_Base64 
            : `data:image/jpeg;base64,${producto.vImagenProducto_Base64}`)
        : "";

    return (
        <Card className="mb-2 card-span border border-200 cursor-pointer">
            <Card.Body className="p-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                
                {/* Lado izquierdo: Imagen, Badges e Info */}
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                    
                    {/* Imagen del producto */}
                    <Avatar 
                        size="2xl" 
                        name={producto.vNombreProducto} 
                        src={imagenSrc} 
                        rounded="soft"
                    />

                    {/* Badges */}
                    <div className="d-flex flex-column align-items-start gap-1" style={{ minWidth: '90px' }}>
                        <Badge
                            bg={agregado ? "success" : "secondary"}
                            pill
                            style={{ fontSize: '0.65rem' }}
                        >
                            {agregado ? <><FaCheckCircle className="me-1" /> Agregado</> : <FaBox className="me-1" />}
                        </Badge>
                    </div>

                    {/* Descripción del producto */}
                    <div style={{ flex: 1 }}>
                        <div className="text-500 fs--2 mb-1 d-flex flex-wrap gap-2">
                            <span><FaBarcode className="me-1" /> {producto.vCodigoBarras || 'N/A'}</span>
                            <span>|</span>
                            <span><strong>Unidad SAT:</strong> {producto.vClaveUnidadSat || 'N/A'}</span>
                            <span>|</span>
                            <span><strong>Cód. SAT:</strong> {producto.vCodigoProductoServicioSat || 'N/A'}</span>
                        </div>
                        <h6
                            className="mb-1 text-800 fw-semi-bold"
                            style={{
                                fontSize: '0.95rem',
                                wordWrap: 'break-word',
                                display: 'block',
                                lineHeight: '1.4',
                            }}
                        >
                            {producto.vNombreProducto}
                        </h6>
                        <div className="text-600 fs--2 d-flex flex-wrap gap-2 mt-1">
                            <span><strong>Precio Unit.:</strong> ${producto.dPrecioUnitario?.toFixed(2) || '0.00'}</span>
                            <span>|</span>
                            <span><strong>Factor IVA:</strong> {producto.vTipoFactorIVA || 'N/A'}</span>
                            <span>|</span>
                            <span><strong>Tasa:</strong> {producto.dTasaCuotaIVA !== null ? producto.dTasaCuotaIVA : 'N/A'}</span>
                            <span>|</span>
                            <span><strong>Monto IVA:</strong> ${producto.dIVA?.toFixed(2) || '0.00'}</span>
                        </div>
                    </div>
                </div>

                {/* Lado derecho: Precio y Botón */}
                <div className="d-flex align-items-center gap-4 justify-content-between justify-content-md-end w-100 w-md-auto">
                    {/* Precio */}
                    <div className="text-end">
                        <span className="text-500 fs--2 d-block">Precio Total</span>
                        <div className="text-primary fw-bold fs-1">
                            ${producto.dTotal?.toFixed(2)} <span className="text-500 fw-normal fs--1">{producto.vMoneda}</span>
                        </div>
                    </div>

                    {/* Botón de acción */}
                    <Button
                        size="sm"
                        variant={agregado ? "warning" : "primary"}
                        className="rounded-2 fw-medium"
                        style={{ minWidth: '140px' }}
                        onClick={() => onAgregarProducto(producto.idProducto)}
                        disabled={cargandoEsteProducto}
                    >
                        {cargandoEsteProducto ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Agregando...
                            </>
                        ) : agregado ? (
                            <>
                                <FaShoppingCart className="me-2" />
                                Agregar otra vez
                            </>
                        ) : (
                            <>
                                <FaShoppingCart className="me-2" />
                                Agregar
                            </>
                        )}
                    </Button>
                </div>

            </Card.Body>
        </Card>
    );
};

ProductoTarjeta.propTypes = {
    producto: PropTypes.shape({
        idProducto: PropTypes.number.isRequired,
        vNombreProducto: PropTypes.string.isRequired,
        vCodigoBarras: PropTypes.string,
        vImagenProducto_Base64: PropTypes.string,
        vClaveUnidadSat: PropTypes.string,
        vCodigoProductoServicioSat: PropTypes.string,
        dPrecioUnitario: PropTypes.number,
        vTipoFactorIVA: PropTypes.string,
        dTasaCuotaIVA: PropTypes.number,
        dIVA: PropTypes.number,
        dTotal: PropTypes.number,
        vMoneda: PropTypes.string
    }).isRequired,
    agregado: PropTypes.bool.isRequired,
    cargandoEsteProducto: PropTypes.bool.isRequired,
    onAgregarProducto: PropTypes.func.isRequired
};

export default ProductoTarjeta;